import classNames from 'classnames'
import useOnClickOutside from 'client/hooks/useOnClickOutside'
import { l } from 'client/Other/Translation'
import { setBaseClass } from 'client/utils'
import * as React from 'react'

import LoadingEllipsis from '../LoadingEllipsis'
import './styles.scss'

interface DropSelectItem {
  id: string | number
}

interface DropSelectProps<T extends DropSelectItem> {
  open: boolean
  automation: string
  items: T[]
  selectedItem: T
  placeholder?: string
  noItemsFoundText?: string
  showSearchIcon?: boolean
  className?: string
  highlight?: boolean
  hasError?: boolean
  maxAllowed?: {
    limit: number
    breaker?: JSX.Element
  }
  onOpen: () => void
  renderItem: (item: T) => JSX.Element | string
  getSelectedText: (item: T) => string
  renderAction?: (item: string) => string | JSX.Element
  onActionClick?: (searchText: string) => void
  onLimitActionClick?: (searchText: string) => void
  sortFn?: (a: T, b: T) => number
  onSelect: (item: T) => void
  onClose: () => void
  onClick: () => void
  onClickOutside: () => void
  renderLogo?: (item: T, cls: (name?: string) => string) => JSX.Element
  isDataLoading?: boolean
  emptyDropSelectText?: string
  disabled?: boolean
  renderAllItemsSelectOption?: () => string | JSX.Element
  onSelectAllItemsClick?: () => void
  searchField?: (value: string) => void
  clearField?: (vakue: string) => void
  inputEditDisabled?: boolean
  isQuotaAvailable?: boolean
}

const cls = setBaseClass('zervantDropSelect')

function DropSelect<T extends DropSelectItem>(props: React.PropsWithChildren<DropSelectProps<T>>): JSX.Element {
  const {
    open,
    automation,
    items,
    placeholder,
    className,
    renderItem,
    noItemsFoundText,
    highlight,
    hasError,
    maxAllowed = { limit: -1 },
    selectedItem,
    showSearchIcon,
    clearField,
    onSelect,
    sortFn,
    onOpen,
    onClose,
    onClick,
    onActionClick,
    renderAction,
    onClickOutside,
    isDataLoading,
    renderLogo,
    emptyDropSelectText,
    disabled,
    renderAllItemsSelectOption,
    onSelectAllItemsClick,
    inputEditDisabled,
    onLimitActionClick,
    searchField,
    getSelectedText,
    isQuotaAvailable,
  } = props

  const getActiveItems = React.useCallback(() => {
    const filtered = maxAllowed.limit < 0 ? items : items.slice(0, maxAllowed.limit)
    if (sortFn) {
      return [...filtered].sort(sortFn)
    }
    return filtered
  }, [items, maxAllowed.limit, sortFn])

  const getInactiveItems = React.useCallback(() => {
    if (maxAllowed.limit < 0) {
      return []
    }
    return items.slice(maxAllowed.limit, items?.length)
  }, [items, maxAllowed.limit])

  const [filterProps, setFilterProps] = React.useState({
    searchText: '',
    filteredActiveItems: getActiveItems(),
    filteredInactiveItems: getInactiveItems(),
  })
  const dropDownRef = React.useRef()
  const inputRef = React.useRef<HTMLInputElement>()
  const [loadOlderItems, setLoadOlderItems] = React.useState(false)

  const getItem = (item: T) => {
    return items.find((i) => i.id === item.id)
  }

  const filterItems = React.useCallback(
    (searchText) => {
      setFilterProps({
        searchText,
        filteredActiveItems: getActiveItems(),
        filteredInactiveItems: getInactiveItems(),
      })
    },
    [getActiveItems, getInactiveItems],
  )

  React.useEffect(() => {
    if (items?.length >= 1000) {
      setLoadOlderItems(true)
    }
    filterItems(filterProps.searchText)
  }, [filterProps.searchText, items?.length])

  React.useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus()
    }
  }, [open])

  useOnClickOutside(dropDownRef, () => {
    onClose()
    if (onClickOutside) {
      onClickOutside()
    }
  })
  return (
    <div ref={dropDownRef} data-automation={automation} className={classNames(cls(), className || '')}>
      <div
        className={classNames(cls('__inputContainer'), {
          [cls('__inputContainer-active')]: open,
          [cls('__inputContainer-highlight')]: highlight,
          [cls('__inputContainer-error')]: hasError,
          [cls('__inputContainer-disabled')]: disabled,
          [cls('__inputContainer-emptyState')]: emptyDropSelectText,
        })}
        onClick={() => {
          onOpen()
          onClick()
          if (inputRef && inputRef.current) {
            inputRef.current.focus()
          }
        }}
      >
        {showSearchIcon && <div className={cls('__searchIcon')} />}
        <div className={cls('__input')}>
          {emptyDropSelectText ? (
            <span>{emptyDropSelectText}</span>
          ) : (
            <input
              ref={inputRef}
              data-automation={`${automation}-input`}
              type="text"
              value={selectedItem ? getSelectedText(getItem(selectedItem)) : filterProps.searchText}
              placeholder={placeholder || ''}
              disabled={inputEditDisabled}
              onChange={(e) => {
                const val = e.target.value
                filterItems(val)
                searchField?.(val)
                if (selectedItem) {
                  onSelect(null)
                }
              }}
            />
          )}
        </div>
        {Boolean(filterProps.searchText || selectedItem) && (
          <div
            className={cls('__clearIcon')}
            onClick={() => {
              filterItems('')
              if (clearField) {
                clearField?.('')
              }
              onSelect(null)
            }}
          />
        )}
        <div
          className={cls('__dropIcon')}
          onClick={(e) => {
            if (open) {
              onClose()
              e.stopPropagation()
            }
          }}
        />
      </div>
      {open && (
        <div className={cls('__menuContainer')}>
          {Boolean(items?.length) &&
            !filterProps.filteredActiveItems?.length &&
            !filterProps.filteredInactiveItems?.length &&
            !isDataLoading && <div className={cls('__menuEmptyMessage')}>{noItemsFoundText || 'No items found'}</div>}
          {isDataLoading && <LoadingEllipsis />}
          {!isDataLoading &&
            Boolean(filterProps.filteredActiveItems?.length || filterProps.filteredInactiveItems?.length) && (
              <div className={cls('__menuItems')}>
                {filterProps.filteredActiveItems.map((item) => {
                  return (
                    <div className={cls('__Item')} key={item.id}>
                      {Boolean(renderLogo) && renderLogo(item, cls)}
                      <div
                        data-automation={`${automation}-option-${item.id}`}
                        className={cls('__menuItem')}
                        onClick={() => {
                          filterItems(getSelectedText(item))
                          onSelect(item)
                          onClose()
                        }}
                      >
                        {renderItem(item)}
                      </div>
                    </div>
                  )
                })}
                {Boolean(filterProps.filteredInactiveItems?.length) && !filterProps.searchText && maxAllowed.breaker}
                {filterProps.filteredInactiveItems.map((item) => (
                  <div
                    key={item.id}
                    data-automation={`${automation}-option-${item.id}`}
                    className={`${cls('__menuItem')} ${cls('__menuItem__inactive')}`}
                  >
                    {renderItem(item)}
                  </div>
                ))}
                {Boolean(renderAllItemsSelectOption) && Boolean(onSelectAllItemsClick) && (
                  <div className={cls('__menuItem')} onClick={() => onSelectAllItemsClick()}>
                    {renderAllItemsSelectOption()}
                  </div>
                )}
              </div>
            )}
          {filterProps.searchText?.length >= 3 && loadOlderItems && isQuotaAvailable ? (
            <div
              className={cls('__menuAction')}
              onClick={() => {
                onLimitActionClick(filterProps.searchText)
              }}
            >
              {l('search.among.olderCustomers')}
            </div>
          ) : (
            ''
          )}

          {Boolean(onActionClick) && Boolean(renderAction) && (
            <div
              className={`${cls('__menuAction')} ${cls('__addCustomer')}`}
              onClick={() => onActionClick(filterProps.searchText)}
            >
              {renderAction(filterProps.searchText)}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default DropSelect
